import React, { useState, useEffect } from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import logoamerisa from '../assets/logoamerisa.png';
import logoamerisazul from '../assets/logoamerisazul.png';
import QRCode from 'qrcode';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    padding: 20,
    position: 'relative',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#003366',
    color: 'white',
    padding: 10,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
  },
  content: {
    marginTop: 10,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 20,
    right: 20,
    textAlign: 'left',
    borderTop: '1px solid #ccc',
    paddingTop: 5,
  },
  footerText: {
    marginBottom: 0,
    lineHeight: 1.2,
  },
  section: {
    marginBottom: 0,
  },
  title: {
    fontSize: 18,
    marginBottom: 10,
  },
  text: {
    marginBottom: 2,
  },
  smallText: {
    marginBottom: 2,
    fontSize: 8,
  },
  smallBold: {
    fontWeight: 'bold',
    fontSize: 8,
  },
  bold: {
    fontWeight: 'bold',
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  column: {
    flex: 1,
    padding: 5,
  },
  borderBottom: {
    borderBottom: '1px solid #ccc',
    marginBottom: 10,
  },
  space: {
    marginBottom: 5,
  },
  extraSpace: {
    marginBottom: 30,
  },
  smallerExtraSpace: {
    marginBottom: 15,
  },
  orderSpace: {
    marginBottom: 10,
  },
  entregaSpace: {
    marginBottom: 50,
  },
  qrCode: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 100,
    height: 100,
  },
  topReference: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 5,
    marginBottom: 10,
  },
  referenceText: {
    fontSize: 12,
  },
  debugBox: {
    position: 'absolute',
    bottom: 5,
    left: 5,
    fontSize: 6,
    color: '#999',
  },
  contactFooter: {
    position: 'absolute',
    bottom: 30,
    left: 20,
    right: 20,
    textAlign: 'left',
    borderTop: '1px solid #ccc',
    paddingTop: 5,
  },
  contactTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    marginTop: 5,
  },
  contactSection: {
    marginBottom: 10,
  },
  contactGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 3,
  },
  contactColumn: {
    width: '25%',
    paddingRight: 5,
    marginBottom: 5,
  },
  contactName: {
    fontSize: 8,
    fontWeight: 'bold',
  },
  contactEmail: {
    fontSize: 8,
  },
  contactPhone: {
    fontSize: 8,
  },
  stopSection: {
    marginTop: 6,
    paddingLeft: 4,
  },
  destinationSpace: {
    marginBottom: 8,
  },
});

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
};

const InstructionLetterPDF = ({
  shipmentData,
  accountName,
  accountRole,
  warehouseData,
  vehicleInfo = null,
  trailerInfo = []
}) => {
  const [qrCodeData, setQrCodeData] = useState({});

  useEffect(() => {
    const generateQrCodes = async () => {
      const qrData = {};
      for (const shipment of shipmentData) {
        const qrCodeUrl = await QRCode.toDataURL(shipment.reference);
        qrData[shipment.reference] = qrCodeUrl;
      }
      setQrCodeData(qrData);
    };
    generateQrCodes();
  }, [shipmentData]);

  const getFormattedDateTime = (order) => {
    if (!order) return 'Sin fecha';
    return `${order.Appointment || ''} ${order.Appointment_hr || ''}`.trim() || 'Sin fecha';
  };

  const getDestinationCity = (order) => {
    if (!order || !order.D_R) {
      return order?.City || 'N/A';
    }
    if (warehouseData && warehouseData.length > 0) {
      const warehouse = warehouseData.find(
        w => w.nombre === order.customer || w.nombre === order.Customer
      );
      if (warehouse && warehouse.ciudad_municipio) {
        return warehouse.ciudad_municipio;
      }
    }
    return order.City || 'N/A';
  };

  const getDestinationAddress = (order) => {
    if (!order || !order.D_R) {
      return order?.address || 'N/A';
    }
    if (warehouseData && warehouseData.length > 0) {
      const warehouse = warehouseData.find(
        w => w.nombre === order.customer || w.nombre === order.Customer
      );
      if (warehouse) {
        return `${warehouse.direccion || ''} ${warehouse.colonia || ''}, ${warehouse.ciudad_municipio || ''}, ${warehouse.estado || ''}`;
      }
    }
    return order?.address || 'N/A';
  };

  const getOriginAddress = (originName, order) => {
    if (order && order.special) {
      const specialAddress = order.special_address || '';
      const specialCity = order.special_city || '';
      const specialState = order.special_state || '';
      if (specialAddress || specialCity || specialState) {
        return `${specialAddress} ${specialCity}, ${specialState}`;
      }
    }
    if (order && order.D_R) {
      return order.address || 'N/A';
    }
    if (!originName) return 'N/A';
    const originParts = originName.split(' , ');
    const actualOriginName = originParts[0];
    if (warehouseData && warehouseData.length > 0) {
      const warehouse = warehouseData.find(w => w.nombre === actualOriginName);
      if (warehouse) {
        return `${warehouse.direccion || ''} ${warehouse.colonia || ''}, ${warehouse.ciudad_municipio || ''}, ${warehouse.estado || ''}`;
      }
    }
    return originName;
  };

  const getVehicleTypeInfo = (shipment) => {
    let vehicleType = shipment.type || 'No especificado';
    let trailerType = 'No especificado';
    let showRemolque1 = false;
    let showRemolque2 = false;
    if (shipment && typeof shipment === 'object') {
      if (shipment.vehicle_type) {
        vehicleType = shipment.vehicle_type;
      } else if (shipment.vehicleType) {
        vehicleType = shipment.vehicleType;
      }
    }
    if (vehicleInfo && typeof vehicleInfo === 'object') {
      vehicleType = vehicleInfo.tipo || vehicleInfo.type || vehicleInfo.vehicleType || vehicleType;
    }
    const isTractorType = vehicleType &&
      (vehicleType.toString().toUpperCase().includes('TRACTO') ||
       vehicleType.toString().toUpperCase().includes('TRACT') ||
       vehicleType.toString().toUpperCase().includes('TRAILER'));
    if (isTractorType) {
      if (shipment.remolque1) {
        showRemolque1 = true;
        if (Array.isArray(trailerInfo) && trailerInfo.length > 0) {
          const trailerFound = trailerInfo.find(t => t && t.placas === shipment.remolque1);
          if (trailerFound) {
            trailerType = trailerFound.tipo || trailerFound.type || trailerType;
          }
        }
      }
      if (shipment.remolque2) {
        showRemolque2 = true;
      }
    }
    return {
      vehicleType,
      trailerType,
      isTractorType,
      showRemolque1,
      showRemolque2
    };
  };

  const getCleanOriginName = (originName) => {
    if (!originName) return 'N/A';
    const originParts = originName.split(' , ');
    return originParts[0];
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logoamerisa} style={{ height: 50 }} />
          <Text style={styles.headerTitle}>CARTA DE INSTRUCCIÓN</Text>
        </View>
        <View style={styles.content}>
          {shipmentData.map((shipment, index) => {
            const uniqueOrigins = Array.from(new Set(shipment.orders.map(order => {
              const warehouse = warehouseData.find(wh => wh.nombre === order.Origin);
              return warehouse ? `${order.Origin} , ${warehouse.ciudad_municipio}` : order.Origin;
            })));
            const totalProductQt = shipment.orders.reduce((total, order) => total + order.productos.reduce((sum, producto) => sum + producto.product_qt, 0), 0);
            const concatenatedDOs = shipment.orders.map(order => order.Order_number).join(', ');
            const totalPesoKg = shipment.orders.reduce((total, order) => {
              const orderPeso = order.productos ? order.productos.reduce((sum, producto) => {
                const pesoProducto = parseFloat(producto.peso_kg) || 0;
                const cantidad = parseInt(producto.product_qt) || 0;
                return sum + (pesoProducto * cantidad);
              }, 0) : 0;
              return total + orderPeso;
            }, 0);
            const {
              vehicleType,
              trailerType,
              isTractorType,
              showRemolque1,
              showRemolque2
            } = getVehicleTypeInfo(shipment);
            const destinations = shipment.orders ? Array.from(new Set(shipment.orders.map(order => order.Stop))).length : 0;
            const useSmallFont = destinations > 1;
            const textStyle = useSmallFont ? styles.smallText : styles.text;
            const boldStyle = useSmallFont ? styles.smallBold : styles.bold;

            return (
              <View key={index}>
                <View style={styles.topReference}>
                  <Text style={styles.referenceText}>Folio de Embarque: {shipment.reference}</Text>
                  <Text style={styles.referenceText}>Truck: {shipment.Truck}</Text>
                </View>
                <View style={styles.grid}>
                  <View style={styles.column}>
                    <Text style={[styles.text, styles.bold]}>DETALLES DE CARGA</Text>
                    <View style={styles.borderBottom}></View>
                    <Text style={styles.text}><Text style={styles.bold}>Fecha de carga:</Text> {formatDate(shipment.pickup_date)}</Text>
                    <View style={styles.infoSpacing}></View>
                    <Text style={styles.text}><Text style={styles.bold}>Origen:</Text></Text>
                    <View>
                      {uniqueOrigins.map((origin, idx) => (
                        <Text key={idx} style={styles.text}>{origin}</Text>
                      ))}
                    </View>
                  </View>
                  <View style={styles.column}>
                    <Text style={[styles.text, styles.bold]}>DETALLES DE ENTREGA</Text>
                    <View style={styles.borderBottom}></View>
                    {(() => {
                      if (!shipment.orders || shipment.orders.length === 0) {
                        return (
                          <>
                            <Text style={styles.text}><Text style={styles.bold}>Fecha de entrega:</Text> Sin información</Text>
                            <View style={styles.infoSpacing}></View>
                            <Text style={styles.text}><Text style={styles.bold}>Cliente:</Text> {shipment.customer || 'N/A'}</Text>
                            <Text style={styles.text}><Text style={styles.bold}>Destino:</Text> {shipment.destination || 'N/A'}</Text>
                          </>
                        );
                      }
                      const groupedOrders = shipment.orders.reduce((acc, order) => {
                        if (!acc[order.Stop]) acc[order.Stop] = [];
                        acc[order.Stop].push(order);
                        return acc;
                      }, {});
                      const entries = Object.entries(groupedOrders).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
                      const useSmallText = entries.length > 1;
                      const textStyle = useSmallText ? styles.smallText : styles.text;
                      const boldStyle = useSmallText ? styles.smallBold : styles.bold;

                      if (entries.length === 1) {
                        const [stop, orders] = entries[0];
                        return (
                          <>
                            <Text style={textStyle}><Text style={boldStyle}>Fecha de entrega:</Text> {formatDate(getFormattedDateTime(orders[0]))}</Text>
                            <View style={styles.infoSpacing}></View>
                            <Text style={textStyle}><Text style={boldStyle}>Cliente:</Text> {orders[0].customer || orders[0].Customer || shipment.customer || 'N/A'}</Text>
                            <Text style={textStyle}><Text style={boldStyle}>Destino:</Text> {getDestinationCity(orders[0])}{orders[0].State ? `, ${orders[0].State}` : ''}</Text>
                          </>
                        );
                      } else {
                        return (
                          <>
                            {entries.map(([stop, orders], index) => (
                              <View key={stop} style={index > 0 ? styles.stopSection : null}>
                                <Text style={textStyle}><Text style={boldStyle}>Destino {stop}:</Text></Text>
                                <Text style={textStyle}> • Cliente: {orders[0].customer || orders[0].Customer || 'N/A'}</Text>
                                <Text style={textStyle}> • Destino: {getDestinationCity(orders[0])}{orders[0].State ? `, ${orders[0].State}` : ''}</Text>
                                <Text style={textStyle}> • Fecha: {formatDate(getFormattedDateTime(orders[0]))}</Text>
                              </View>
                            ))}
                          </>
                        );
                      }
                    })()}
                  </View>
                  <View style={styles.column}>
                    <Text style={[styles.text, styles.bold]}>LINEA TRANSPORTISTA</Text>
                    <View style={styles.borderBottom}></View>
                    <Text style={styles.text}><Text style={styles.bold}>Nombre de la línea:</Text> {shipment.Lt}</Text>
                    <Text style={styles.text}><Text style={styles.bold}>Nombre del operador:</Text> {shipment.driver}</Text>
                    <Text style={styles.text}><Text style={styles.bold}>Teléfono del operador:</Text> {shipment.driver_phone || 'No disponible'}</Text>
                    <Text style={styles.text}><Text style={styles.bold}>Vehículo:</Text> {shipment.vehicle}</Text>
                    {isTractorType ? (
                      <>
                        {showRemolque1 && (
                          <Text style={styles.text}><Text style={styles.bold}>Remolque 1:</Text> {shipment.remolque1}</Text>
                        )}
                        <Text style={styles.text}><Text style={styles.bold}>Tipo:</Text> {vehicleType}</Text>
                        {showRemolque2 && (
                          <Text style={styles.text}><Text style={styles.bold}>Remolque 2:</Text> {shipment.remolque2}</Text>
                        )}
                      </>
                    ) : (
                      <>
                        <Text style={styles.text}><Text style={styles.bold}>Tipo:</Text> {vehicleType}</Text>
                        <Text style={styles.text}><Text style={styles.bold}>Vehículo:</Text> {shipment.vehicle}</Text>
                      </>
                    )}
                    {shipment.marchamo1 && shipment.marchamo1.trim() !== '' && (
                      <Text style={styles.text}><Text style={styles.bold}>Marchamo 1:</Text> {shipment.marchamo1}</Text>
                    )}
                    {shipment.marchamo2 && shipment.marchamo2.trim() !== '' && (
                      <Text style={styles.text}><Text style={styles.bold}>Marchamo 2:</Text> {shipment.marchamo2}</Text>
                    )}
                  </View>
                </View>
                <View style={styles.smallerExtraSpace}></View>
                <Text style={styles.sectionTitle}>Órdenes Vinculadas</Text>
                <View style={styles.section}>
                  <Text style={useSmallFont ? styles.smallBold : styles.bold}>Origen(es):</Text>
                  {(() => {
                    const uniqueOrigins = new Map();
                    shipment.orders && shipment.orders.forEach(order => {
                      const originName = order.Origin;
                      const originAddress = getOriginAddress(originName, order);
                      const cleanName = getCleanOriginName(originName);
                      const key = `${cleanName}:${originAddress}`;
                      if (!uniqueOrigins.has(key)) {
                        uniqueOrigins.set(key, { name: cleanName, address: originAddress });
                      }
                    });
                    return Array.from(uniqueOrigins.values()).map((origin, idx) => (
                      <View key={`unique-origin-${idx}`} style={{ marginBottom: 5 }}>
                        <Text style={textStyle}>
                          <Text style={boldStyle}>{origin.name}:</Text> {origin.address}
                        </Text>
                      </View>
                    ));
                  })()}
                </View>
                <View style={styles.space}></View>
                {shipment.orders && Object.entries(
                  shipment.orders.reduce((acc, order) => {
                    if (!acc[order.Stop]) acc[order.Stop] = [];
                    acc[order.Stop].push(order);
                    return acc;
                  }, {})
                ).map(([stop, orders], index, array) => {
                  const concatenatedDOs = orders.map(order => order.Order_number).join(', ');
                  const totalProductQt = orders.reduce((total, order) => total + order.productos.reduce((sum, producto) => sum + producto.product_qt, 0), 0);
                  const productDetails = orders.flatMap(order => order.productos.map(producto => {
                    const descripcion = producto.descripcion || 'Descripción no disponible';
                    const peso_kg = producto.peso_kg || 'N/A';
                    const volumen_m3 = producto.volumen_m3 || 'N/A';
                    return `${descripcion} (Peso: ${peso_kg} kg, Volumen: ${volumen_m3} m3, Cantidad: ${producto.product_qt})`;
                  })).join(', ');
                  const isLastItem = index === array.length - 1;
                  return (
                    <View key={stop}>
                      <View style={styles.section}>
                        <Text style={textStyle}><Text style={boldStyle}>Destino {stop}:</Text></Text>
                        <Text style={textStyle}><Text style={boldStyle}>Dirección destino:</Text> {getDestinationAddress(orders[0])}</Text>
                        <Text style={textStyle}><Text style={boldStyle}>DO:</Text> {concatenatedDOs}</Text>
                        <Text style={textStyle}><Text style={boldStyle}>Cantidad Total:</Text> {totalProductQt}</Text>
                      </View>
                      {!isLastItem && <View style={styles.destinationSpace}></View>}
                    </View>
                  );
                })}
                <View style={styles.smallerExtraSpace}></View>
                <Text style={styles.title}>Requerimientos Obligatorios</Text>
                <View style={styles.section}>
                  <Text style={styles.text}><Text style={styles.bold}>Requerimientos:</Text> {shipment.requerimientos}</Text>
                  <View style={styles.space}></View>
                  <Text style={styles.text}>* En caso de no cumplirse Amerisa Logistics se reserva el derecho de no llevar a cabo el embarque o aplicar las sanciones correspondientes</Text>
                </View>
                <View style={styles.extraSpace}></View>
                <View style={styles.grid}>
                  <View style={styles.column}>
                    <Text style={styles.title}>Detalles Carta Porte</Text>
                    <View style={styles.section}>
                      <Text style={styles.text}><Text style={styles.bold}>Razón Social:</Text> Al Transportes</Text>
                      <Text style={styles.text}><Text style={styles.bold}>RFC:</Text> TPE1202221Z0</Text>
                      <Text style={styles.text}><Text style={styles.bold}>Mercancía:</Text> Línea Blanca</Text>
                      <Text style={styles.text}><Text style={styles.bold}>Número de piezas:</Text> {totalProductQt}</Text>
                      <Text style={styles.text}><Text style={styles.bold}>Código SAT de la mercancía:</Text> 52141500</Text>
                      <Text style={styles.text}><Text style={styles.bold}>Truck:</Text> {shipment.Truck}</Text>
                      <Text style={styles.text}><Text style={styles.bold}>DO:</Text> {concatenatedDOs}</Text>
                      <Text style={styles.text}><Text style={styles.bold}>Peso:</Text> {totalPesoKg.toFixed(2)} kg</Text>
                    </View>
                  </View>
                </View>
                {qrCodeData[shipment.reference] && (
                  <Image src={qrCodeData[shipment.reference]} style={styles.qrCode} />
                )}
              </View>
            );
          })}
        </View>
        <View style={styles.footer}>
          <Text style={styles.footerText}><Text style={styles.bold}>Generado por:</Text> {accountName}</Text>
          <Text style={styles.footerText}><Text style={styles.bold}>Puesto:</Text> {accountRole}</Text>
        </View>
      </Page>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image src={logoamerisa} style={{ height: 50 }} />
          <Text>Condiciones Generales</Text>
        </View>
        <View style={styles.content}>
          <Text style={styles.text}>Se deberá cumplir con todos los lineamientos que nos solicita la empresa, de lo contrario NO PODRÁN INGRESAR a las instalaciones:</Text>
          <View style={styles.space}></View>
          <Text style={styles.title}>Equipo de proteccion personal (EPP)</Text>
          <Text style={styles.text}>Zapatos de seguridad</Text>
          <Text style={styles.text}>Lentes de seguridad</Text>
          <Text style={styles.text}>Casco</Text>
          <Text style={styles.text}>Chaleco reflejante </Text>
          <Text style={styles.text}>Credencial de su empresa (para utilizar como identificación)</Text>
          <Text style={styles.text}>INE vigente</Text>
          <View style={styles.space}></View>
          <Text style={styles.text}>Así mismo, la línea de Transporte deberá proporcionar cuenta espejo en el Grupo de WhatsApp y a los siguientes correos:</Text>
          <View style={styles.space}></View>
          <Text style={styles.text}>• centrodemonitoreo@amerisa.com.mx</Text>
          <Text style={styles.text}>• centrodemonitoreo2@amerisa.com.mx</Text>
          <Text style={styles.text}>• centrodemonitoreo3@amerisa.com.mx</Text>
          <Text style={styles.text}>• centrodemonitoreo4@amerisa.com.mx</Text>
          <View style={styles.space}></View>
          <Text style={styles.title}>Especificaciones de la unidad:</Text>
          <Text style={styles.text}>Las unidades deben contar con póliza de seguro vigente.</Text>
          <Text style={styles.text}>Realizar una inspección de unidad previa a cita de carga asegurando que la unidad este en condiciones óptimas para carga.</Text>
          <Text style={styles.text}>Las unidades deberán traer como mínimo 2 equipos de sujeción en buen estado de acuerdo al tipo de caja.</Text>
          <Text style={styles.text}>Las unidades deberán presentarse en condiciones óptimas para la carga del producto, es decir, no deberán presentar filtraciones de luz o agua, laminas o maderas levantas, con manchas de grasa, paredes y/o piso dañados, olores extraños, húmedas, sucias o con fauna nociva, etc.</Text>
          <Text style={styles.text}>Las unidades no deben ser refrigeradas.</Text>
          <View style={styles.space}></View>
          <Text style={styles.title}>Especificaciones del operador:</Text>
          <Text style={styles.text}>El operador debe portar: vestimenta limpia y sin roturas, playera lisa, zapatos de seguridad, credencial de su empresa, casco, chaleco reflejante</Text>
          <Text style={styles.text}>El operador no puede fumar, beber ni masticar chicle durante el servicio.</Text>
          <Text style={styles.text}>El operador deberá contar con licencia vigente e INE.</Text>
          <Text style={styles.text}>Los operadores deberán estar atentos a instrucciones y servicio al cliente.</Text>
          <Text style={styles.text}>Los operadores tiene prohibido tomar fotografías dentro de los cedis.</Text>
          <View style={styles.space}></View>
          </View>
        <View style={styles.contactFooter}>
          <Text style={styles.contactTitle}>Contacto</Text>
          <View style={styles.contactSection}>
            <Text style={styles.contactTitle}>Procuración</Text>
            <View style={styles.contactGrid}>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Rafael Campos</Text>
                <Text style={styles.contactEmail}>j_campos@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 55 2109 7185</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Fidel Morales</Text>
                <Text style={styles.contactEmail}>l_morales@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 55 8064 3166</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Alexia Cuevas</Text>
                <Text style={styles.contactEmail}>trafico_tul2@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 55 3438 1281</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Israel Silva</Text>
                <Text style={styles.contactEmail}>i_silva@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 55 7856 7700</Text>
              </View>
            </View>
          </View>
          <View style={styles.contactSection}>
            <Text style={styles.contactTitle}>Tráfico</Text>
            <View style={styles.contactGrid}>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Diego Diaz</Text>
                <Text style={styles.contactEmail}>d_diaz@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 999 649 0054</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Carolina Perez</Text>
                <Text style={styles.contactEmail}>trafico_tul@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 999 233 6984</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Trafico Teoloyucan</Text>
                <Text style={styles.contactEmail}>aux_trafico@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 55 1849 3669</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Trafico Texcoco</Text>
                <Text style={styles.contactEmail}>aux_traficotex@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 55 4056 3941</Text>
              </View>
            </View>
          </View>
          <View style={styles.contactSection}>
            <Text style={styles.contactTitle}>Torre de Control</Text>
            <View style={styles.contactGrid}>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Luis Dzul</Text>
                <Text style={styles.contactEmail}>centrodemoniroreo@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 999 357 0017</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Ricardo Velueta</Text>
                <Text style={styles.contactEmail}>centrodemonitoreo3@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 999 271 6302</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Augusto Negron</Text>
                <Text style={styles.contactEmail}>centrodemonitoreo2@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 999 368 0938</Text>
              </View>
              <View style={styles.contactColumn}>
                <Text style={styles.contactName}>Gema Poot</Text>
                <Text style={styles.contactEmail}>centrodemonitoreo4@amerisa.com.mx</Text>
                <Text style={styles.contactPhone}>+52 999 313 8145</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InstructionLetterPDF;